// application/src/services/versionChecker.js
import axios from 'axios';

let currentAppVersion = null;
let newVersionAvailable = false;
let updateSnackbarHandler = null; // Callback to show snackbar in App.vue
let isChecking = false; // Flag to prevent multiple simultaneous checks

/**
 * Fetches the version from the version.json file on the server.
 * Uses a cache-busting query parameter.
 * @returns {Promise<string|null>} The version string or null if an error occurs.
 */
async function fetchServerVersion() {
  // Prevent version check on localhost or 127.0.0.1
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return currentAppVersion; // Return known version or null, do not proceed to fetch
  }

  try {
    // Fetch the version.json file from the server with a cache-busting query param
    const response = await axios.get(`/version.json?t=${new Date().getTime()}`);
    const serverVersion = response.data.version;
    if (currentAppVersion && serverVersion !== currentAppVersion) {
      newVersionAvailable = true;
      if (typeof updateSnackbarHandler === 'function') {
        updateSnackbarHandler();
      }
    } else if (!currentAppVersion) {
      // Store the first version fetched if none is set
      currentAppVersion = serverVersion;
    }
    return serverVersion;
  } catch (error) {
    // If version.json is not found or another error occurs, assume an update might be needed.
    // This can happen during deployments if the old client tries to fetch a new, not-yet-fully-deployed version.json
    // or if the file is temporarily unavailable.
    newVersionAvailable = true;
    if (typeof updateSnackbarHandler === 'function') {
      updateSnackbarHandler();
    }
    return null;
  }
}

/**
 * Checks for application updates.
 * Compares the current application version with the version on the server.
 * If a new version is available, it calls the registered snackbar handler.
 */
export async function checkForUpdates() {
  if (document.hidden || isChecking) { // Don't check if the tab is not visible or already checking
    return;
  }

  isChecking = true;
  await fetchServerVersion();
  isChecking = false;
}

/**
 * Initializes the version checker.
 * Fetches the initial version and sets up listeners for browser events.
 */
export function initializeVersionChecker() {
  // Fetch initial version shortly after app loads to not block initial render
  setTimeout(async () => {
    await fetchServerVersion();
    // Start checking for updates after initial version is fetched or attempt failed
    checkForUpdates(); // Initial check
  }, 1000); // Delay slightly

  // Check for updates when the tab becomes visible or gets focus
  document.addEventListener('visibilitychange', checkForUpdates);
  window.addEventListener('focus', checkForUpdates);
}

/**
 * Registers a callback function to be called when a new version is detected.
 * This function is typically used to show an update notification (e.g., a snackbar).
 * @param {Function} handler - The callback function.
 */
export function onUpdateAvailable(handler) {
  updateSnackbarHandler = handler;
  // If an update was already detected before handler was set
  if (newVersionAvailable && typeof updateSnackbarHandler === 'function') {
    updateSnackbarHandler();
  }
}

/**
 * Called by the router when a chunk load error occurs.
 * This is a strong indicator that a new version might be live.
 */
export function handleChunkLoadError() {
  newVersionAvailable = true; // Assume new version is available
  if (typeof updateSnackbarHandler === 'function') {
    updateSnackbarHandler();
  }
}

/**
 * Cleans up event listeners.
 * Should be called when the main application component is destroyed.
 */
export function cleanupVersionChecker() {
  document.removeEventListener('visibilitychange', checkForUpdates);
  window.removeEventListener('focus', checkForUpdates);
  updateSnackbarHandler = null;
  currentAppVersion = null;
  newVersionAvailable = false;
  isChecking = false;
}
